import { Box, Button, Typography, Grid, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { DrawerHeader } from '../../../components/Drawer/DrawerHeader';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import {
    useGetExamenesRiesgoQuery,
    useAsignarExamenEmpleadosMutation
} from '../../../services/periodicosApi';
import { enqueueSnackbar } from 'notistack';
import { ExamenesRiesgoResponse } from '../consts/periodicosTypes';
import { showAlert } from '../../../features/slices/applicationSlice';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import { CircularProgress } from '@mui/material';

interface PeriodicosExamDrawerProps {
    selectedIds: string[];
    setOpen: (state: string) => void;
    reloadGrid: (goToNextTab?: boolean) => void;
}

const PeriodicosExamDrawer = ({ selectedIds, setOpen, reloadGrid }: PeriodicosExamDrawerProps) => {
    const { t } = useTranslation('periodicos');
    const idRiesgo = Number(selectedIds[0]?.split('-')[1]);
    const dispatch = useAppDispatch();

    const [asignarExamen, { isLoading: isSaving }] = useAsignarExamenEmpleadosMutation();

    const { data: examsResponse, isLoading } = useGetExamenesRiesgoQuery(idRiesgo, {
        skip: !idRiesgo
    }) as { data?: ExamenesRiesgoResponse; isLoading: boolean };

    const examsData = examsResponse?.examenes || [];

    const examOptions = examsData.map((exam) => ({
        id: exam.examenMedico.id,
        nombre: exam.examenMedico.descripcion,
        frecuencia: exam.examenMedico.frecuencia,
        cantidad: exam.cantidad
    }));

    const [selectedExam, setSelectedExam] = useState<any>(null);
    const [selectedExams, setSelectedExams] = useState<typeof examOptions>([]);
    const [examToDelete, setExamToDelete] = useState<number | null>(null);

    useEffect(() => {
        setSelectedExams(examOptions);
    }, [examsResponse]);

    const handleConfirmDelete = () => {
        if (examToDelete !== null) {
            setSelectedExams((prev) => prev.filter((e) => e.id !== examToDelete));
            setExamToDelete(null);
        }
    };

    const handleRemoveExam = (id: number) => {
        const exam = selectedExams.find((e) => e.id === id);
        if (!exam) return;

        dispatch(
            showAlert({
                title: t('drawer.exam.confirmDeleteTitle'),
                message: t('drawer.exam.confirmDeleteMessage'),
                icon: 'info',
                confirmText: t('drawer.exam.confirmButton'),
                cancelText: t('drawer.exam.cancelButton'),
                confirmAction: () => {
                    setSelectedExams((prev) => prev.filter((e) => e.id !== id));
                }
            })
        );
    };

    const handleCloseDrawer = () => {
        setOpen('');
    };

    const handleSave = async () => {
        const idsEmpleados = selectedIds.map((str) => Number(str.split('-')[0]));

        if (selectedExams.length === 0 || idsEmpleados.length === 0 || !idRiesgo) {
            enqueueSnackbar({
                message: t('enqueue.assignExam.missingData'),
                variant: 'error'
            });
            return;
        }

        try {
            for (const exam of selectedExams) {
                await asignarExamen({
                    idExamen: exam.id,
                    payload: { idsEmpleados, idRiesgo }
                }).unwrap();
            }

            enqueueSnackbar({
                message: t('enqueue.assignExam.success'),
                variant: 'success'
            });
            setOpen('');
            reloadGrid(true);
        } catch (err: any) {
            enqueueSnackbar({
                message: err?.data?.message || t('enqueue.assignExam.error'),
                variant: 'error'
            });
        }
    };

    return (
        <>
            <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <Grid container flexDirection="column">
                    <DrawerHeader onIconClick={handleCloseDrawer} text={t('drawer.exam.title')} />

                    {isLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" mt={5}>
                            <CircularProgress />
                        </Box>
                    ) : examOptions.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" mt={5}>
                            <Typography variant="body2" fontStyle="italic">
                                {t('drawer.exam.noExams') ||
                                    'No existen exámenes para este riesgo.'}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {selectedExams.length < examOptions.length && (
                                <Grid container spacing={2} alignItems="center" mt={1}>
                                    <Grid item xs={12}>
                                        <AutocompleteComponent
                                            label={t('drawer.exam.select')}
                                            size="small"
                                            name="examSelector"
                                            options={examOptions.filter(
                                                (opt) => !selectedExams.some((e) => e.id === opt.id)
                                            )}
                                            value={selectedExam}
                                            getOptionLabel={(option: any) =>
                                                `${option.nombre} - ${option.frecuencia} - ${option.cantidad}`
                                            }
                                            onChange={(value: any) => {
                                                if (value) {
                                                    setSelectedExams((prev) => [...prev, value]);
                                                    setSelectedExam(null);
                                                }
                                            }}
                                            clearable
                                            loading={isLoading}
                                            disableCloseOnSelect={true}
                                            filterSelectedOptions={false}
                                        />
                                    </Grid>
                                </Grid>
                            )}

                            <Grid container spacing={2} mt={2}>
                                {selectedExams.map((exam: any) => (
                                    <Grid item xs={12} key={exam.id}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: '8px',
                                                backgroundColor: '#E0EBED',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {exam.nombre}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <Box component="span" fontWeight="bold">
                                                        Frecuencia:
                                                    </Box>{' '}
                                                    {exam.frecuencia}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <Box component="span" fontWeight="bold">
                                                        Cantidad:
                                                    </Box>{' '}
                                                    {exam.cantidad}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                onClick={() => handleRemoveExam(exam.id)}
                                                color="error"
                                                sx={{
                                                    backgroundColor: '#ffffffc9',
                                                    borderRadius: '5px',
                                                    border: '1px solid #e4e4e4',
                                                    padding: '6px',
                                                    '&:hover': {
                                                        backgroundColor: '#f0f0f0'
                                                    }
                                                }}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="outlined" onClick={handleCloseDrawer} sx={{ mr: 1 }}>
                        {t('drawer.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={selectedExams.length === 0 || isSaving}
                        sx={{ textTransform: 'none', minWidth: 100 }}
                    >
                        {isSaving ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            t('drawer.save')
                        )}
                    </Button>
                </Box>
            </Box>
        </>
    );
};

export default PeriodicosExamDrawer;
